/**
 * usePlanSelection — Plan-Auswahl-Hook
 *
 * Nimmt Plan-Auswahl-Events entgegen, speichert sie in localStorage
 * und triggert eine Bestätigungs-Toast-Meldung.
 *
 * Speichert in denselben Key wie useFeatureGate ("foerderfuchs_current_plan"),
 * damit featureGates nach einem Reload automatisch den neuen Plan reflektieren.
 * Beide Hooks müssen denselben PLAN_KEY verwenden — bei Umbenennungen beide anpassen.
 *
 * Migration path:
 *   Heute:  localStorage.setItem(PLAN_KEY, planId)
 *   Morgen: POST /api/subscriptions/select + Auth-Context-Update
 *   → Nur selectPlan() anpassen — alle Aufrufer bleiben unverändert.
 *
 * Exports:
 *   usePlanSelection()  → { selectedPlanId, selectPlan }
 *   useTrialStatus()    → { isActive, daysRemaining, daysElapsed, startedAt }
 */

import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { getPlanById, trialConfig } from "../data/pricing";
import { REQUIRE_AUTH, isAuthenticated } from "../lib/authToken";
import { trackEvent } from "../lib/trackEvent";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Gleicher Key wie in useFeatureGate.js — muss konsistent bleiben.
const PLAN_KEY = "foerderfuchs_current_plan";

// Trial-Startzeitpunkt (Unix-Timestamp als String).
// Wird beim ersten selectPlan()-Aufruf oder bei der Registrierung gesetzt, nie überschrieben.
const TRIAL_KEY = "foerderfuchs_trial_started_at";

/**
 * Startet die 14-Tage-Trial-Uhr im Client, falls noch nicht gestartet.
 * Spiegel des serverseitigen Trials (app_account.trial_ends_at), der bei der
 * Account-Anlage gesetzt wird — hier nur fürs UI (Feature-Gates, Trial-Banner).
 */
export function startTrialIfAbsent() {
  try {
    if (!localStorage.getItem(TRIAL_KEY)) {
      localStorage.setItem(TRIAL_KEY, Date.now().toString());
    }
  } catch (_) { /* privater Modus o.ä. — Server-Trial gilt trotzdem */ }
}

// ── usePlanSelection ──────────────────────────────────────────────────────────

// Gewählter Abrechnungszeitraum ("monthly" | "yearly") — vom Pricing-Toggle gesetzt,
// damit Checkout und spätere Upgrade-Touchpoints (Settings) dieselbe Wahl nutzen.
const PERIOD_KEY = "foerderfuchs_billing_period";

function storedBillingPeriod() {
  const v = localStorage.getItem(PERIOD_KEY);
  return v === "yearly" ? "yearly" : "monthly";
}

/**
 * Hook für Plan-Auswahl-Events aus der PricingPage.
 *
 * @returns {{
 *   selectedPlanId: string|null,           — aktive Plan-ID (aus localStorage)
 *   selectPlan:     (planId: string, opts?: {billingPeriod?: "monthly"|"yearly"}) => void
 * }}
 */
export function usePlanSelection() {
  // Initialisiert mit dem aktuell gespeicherten Plan (falls vorhanden).
  // Ermöglicht sofortige Anzeige des "gewählt"-Zustands beim Seitenaufruf.
  const [selectedPlanId, setSelectedPlanId] = useState(
    () => localStorage.getItem(PLAN_KEY) ?? null
  );

  const selectPlan = async (planId, { billingPeriod } = {}) => {
    const plan = getPlanById(planId);
    // Rückgabe: true, wenn der Aufruf eine Weiterleitung (Stripe/Login/Kontakt) ausgelöst
    // hat → der Aufrufer soll dann NICHT zusätzlich navigieren. false = nur lokal vorgemerkt.
    if (!plan) return false; // defensive: unbekannte Plan-ID ignorieren

    // Anfrage-Stufen (Enterprise): bewusst KEIN Checkout — Preis entsteht im Gespräch.
    // Greift für alle Aufrufer (PricingPage, SettingsPage, ?plan=-Deep-Link nach Login),
    // damit nirgends ein Festpreis-Checkout für eine "Individuell"-Stufe startet.
    if (plan.requestOnly) {
      trackEvent("plan_request_started", { plan: planId, authenticated: isAuthenticated() });
      window.location.assign(plan.requestTo || "/kontakt");
      return true;
    }

    // Abrechnungszeitraum: expliziter Toggle-Wert > zuletzt gespeicherte Wahl > monatlich.
    // Muss dem Checkout mitgegeben werden — sonst bekämen Jahreszahler ein Monats-Abo.
    const period = billingPeriod === "yearly" || billingPeriod === "monthly"
      ? billingPeriod : storedBillingPeriod();
    try { localStorage.setItem(PERIOD_KEY, period); } catch (_) { /* ignore */ }

    // Funnel: Kaufabsicht (vor Auth-/Checkout-Verzweigung) — Conversion-Messung.
    trackEvent("plan_selected", { plan: planId, period, authenticated: isAuthenticated() });

    // 0. Ausgeloggter Besucher (öffentliche Pricing-Seite): NICHT den Checkout aufrufen
    //    (würde 401/403 werfen). Stattdessen Plan vormerken und zur Registrierung führen.
    if (REQUIRE_AUTH && !isAuthenticated()) {
      try { localStorage.setItem(PLAN_KEY, planId); } catch (_) { /* ignore */ }
      window.location.assign(`/login?tab=register&plan=${encodeURIComponent(planId)}`);
      return true;
    }

    // 1. Echte Bezahlung versuchen (Stripe-Checkout). Bei Erfolg → Redirect zu Stripe.
    try {
      const res = await axios.post(`${API}/billing/checkout`, {
        plan_tier: planId,
        billing_period: period,
      });
      if (res?.data?.checkout_url) {
        window.location.assign(res.data.checkout_url);
        return true;
      }
      // Tarifwechsel bei bereits aktivem Abo: kein neuer Checkout, sondern Umstellung.
      if (res?.data?.updated) {
        try { localStorage.setItem(PLAN_KEY, planId); } catch (_) {}
        setSelectedPlanId(planId);
        toast.success(res.data.message || "Dein Tarif wurde geändert.");
        return true;
      }
    } catch (e) {
      const status = e?.response?.status;
      // 409 = Tarif bereits aktiv → freundlich melden, kein Fehler-Ton.
      if (status === 409) {
        toast.info(e?.response?.data?.detail || "Dieser Tarif ist bereits aktiv.");
        return true;
      }
      // 503 = Stripe noch nicht konfiguriert → ehrlicher Fallback (Vorauswahl merken)
      if (status && status !== 503) {
        toast.error(e?.response?.data?.detail || "Buchung konnte nicht gestartet werden.");
        return false;
      }
    }

    // 2. Fallback (Stripe inaktiv): Auswahl lokal merken, ehrliche Meldung.
    localStorage.setItem(PLAN_KEY, planId);
    setSelectedPlanId(planId);
    startTrialIfAbsent();

    toast.success(`${plan.name} vorgemerkt`, {
      description: "Die Online-Bezahlung wird in Kürze aktiviert — wir melden uns zur Freischaltung.",
      duration: 5000,
    });
    return false;   // keine Weiterleitung → Aufrufer navigiert selbst weiter
  };

  return { selectedPlanId, selectPlan };
}

// ── useTrialStatus ────────────────────────────────────────────────────────────

/**
 * Gibt den aktuellen Frontend-Trial-Status zurück.
 *
 * Reine localStorage-Berechnung — kein Backend, kein Async.
 * Startzeitpunkt wird von selectPlan() geschrieben (TRIAL_KEY).
 *
 * Migration path:
 *   Heute:  localStorage.getItem(TRIAL_KEY)
 *   Morgen: currentUser.subscription?.trialStartedAt  (aus Auth-Context/API)
 *   → Nur diese Funktion anpassen — alle Aufrufer bleiben unverändert.
 *
 * @returns {{
 *   isActive:      boolean,      — true wenn Testphase noch läuft
 *   daysRemaining: number|null,  — verbleibende Tage (null wenn noch nicht gestartet)
 *   daysElapsed:   number|null,  — vergangene Tage seit Start
 *   startedAt:     number|null,  — Unix-Timestamp des Trial-Starts
 * }}
 */
export function useTrialStatus() {
  const startedAtStr = localStorage.getItem(TRIAL_KEY);

  if (!startedAtStr || !trialConfig.enabled) {
    return { isActive: false, daysRemaining: null, daysElapsed: null, startedAt: null };
  }

  const startedAt = parseInt(startedAtStr, 10);
  if (isNaN(startedAt)) {
    return { isActive: false, daysRemaining: null, daysElapsed: null, startedAt: null };
  }

  const daysElapsed = Math.floor((Date.now() - startedAt) / (1000 * 60 * 60 * 24));
  const daysRemaining = Math.max(0, trialConfig.days - daysElapsed);
  const isActive = daysRemaining > 0;

  return { isActive, daysRemaining, daysElapsed, startedAt };
}
